import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Player from "video.js/dist/types/player";

// Context
import { VideoPlayerSettingsContext } from "../../../../Context/VideoPlayerSettingsContext";

export interface ISubtitleTrackOption {
    label: string;
    language: string;
}

// VHS exposes manifest subtitle renditions (WebVTT) as text tracks alongside
// internal tracks like "segment-metadata", so filter to caption-style kinds only
const getSubtitleTracks = (player: Player): TextTrack[] => {
    const trackList = (player as any).textTracks();
    const tracks: TextTrack[] = [];

    for (let i = 0; i < trackList.length; i++) {
        const track = trackList[i];
        if (track.kind === "subtitles" || track.kind === "captions") {
            tracks.push(track);
        }
    }

    return tracks;
};

// Tracks aren't guaranteed a language code, so fall back to the label as the identifier
const getTrackId = (track: TextTrack): string => track.language || track.label;

const pickPreferredTrack = (tracks: TextTrack[], savedLanguage: string): TextTrack => {
    return (
        tracks.find((track) => getTrackId(track) === savedLanguage) ??
        tracks.find((track) => (track as any).default) ??
        tracks[0]
    );
};

const useSubtitleTracks = (player: Player | null) => {
    const {
        subtitlesEnabled,
        setSubtitlesEnabled,
        subtitlesLanguage,
        setSubtitlesLanguage
    } = useContext(VideoPlayerSettingsContext);

    const [trackOptions, setTrackOptions] = useState<ISubtitleTrackOption[]>([]);
    const [activeLanguage, setActiveLanguage] = useState("");

    const subtitlesEnabledRef = useRef(subtitlesEnabled);
    const subtitlesLanguageRef = useRef(subtitlesLanguage);
    const hasAppliedPreferenceRef = useRef(false);

    useEffect(() => {
        subtitlesEnabledRef.current = subtitlesEnabled;
        subtitlesLanguageRef.current = subtitlesLanguage;
    }, [subtitlesEnabled, subtitlesLanguage]);

    useEffect(() => {
        if (!player) return;
        const trackList = (player as any).textTracks();
        hasAppliedPreferenceRef.current = false;

        // VHS lazy-loads subtitle renditions and only reacts to mode changes
        // made once metadata has loaded — enabling a track before that point
        // marks it "showing" without its cues ever being fetched
        const applySavedPreference = () => {
            if (hasAppliedPreferenceRef.current) return;
            if (((player as any).readyState() ?? 0) < 1) return;

            const subtitleTracks = getSubtitleTracks(player);
            if (subtitleTracks.length === 0) return;

            hasAppliedPreferenceRef.current = true;
            if (subtitlesEnabledRef.current) {
                const preferred = pickPreferredTrack(subtitleTracks, subtitlesLanguageRef.current);
                subtitleTracks.forEach((track) => {
                    track.mode = track === preferred ? "showing" : "disabled";
                });
            }
        };

        const syncTrackState = () => {
            applySavedPreference();

            const subtitleTracks = getSubtitleTracks(player);
            setTrackOptions(
                subtitleTracks.map((track) => ({
                    label: track.label || track.language || "Subtitles",
                    language: getTrackId(track),
                }))
            );

            const showingTrack = subtitleTracks.find((track) => track.mode === "showing");
            setActiveLanguage(showingTrack ? getTrackId(showingTrack) : "");
        };

        syncTrackState();
        trackList.on("addtrack", syncTrackState);
        trackList.on("removetrack", syncTrackState);
        trackList.on("change", syncTrackState);
        player.on("loadedmetadata", syncTrackState);

        return () => {
            trackList.off("addtrack", syncTrackState);
            trackList.off("removetrack", syncTrackState);
            trackList.off("change", syncTrackState);
            player.off("loadedmetadata", syncTrackState);
        };
    }, [player]);

    // Pass a track's language to enable it, or "" to turn subtitles off
    const selectSubtitles = useCallback(
        (language: string) => {
            if (!player) return;
            const subtitleTracks = getSubtitleTracks(player);
            if (subtitleTracks.length === 0) return;

            const target = language
                ? subtitleTracks.find((track) => getTrackId(track) === language) ?? null
                : null;

            subtitleTracks.forEach((track) => {
                track.mode = track === target ? "showing" : "disabled";
            });

            setSubtitlesEnabled(target !== null);
            if (target) {
                // Remember the language so quick-toggling off and on restores it
                setSubtitlesLanguage(getTrackId(target));
            }
        },
        [player, setSubtitlesEnabled, setSubtitlesLanguage]
    );

    const toggleSubtitles = useCallback(() => {
        if (!player) return;
        const subtitleTracks = getSubtitleTracks(player);
        if (subtitleTracks.length === 0) return;

        const anyShowing = subtitleTracks.some((track) => track.mode === "showing");
        if (anyShowing) {
            selectSubtitles("");
        } else {
            selectSubtitles(getTrackId(pickPreferredTrack(subtitleTracks, subtitlesLanguageRef.current)));
        }
    }, [player, selectSubtitles]);

    return {
        subtitleTracks: trackOptions,
        activeLanguage,
        isShowing: activeLanguage !== "",
        selectSubtitles,
        toggleSubtitles,
    };
};

export default useSubtitleTracks;
